import { Link } from '@tanstack/react-router'
import { Receipt } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { OrderStatusBadge, methodLabel } from '@/components/ui/status'
import { useOrders } from '@/features/payment/api'
import { formatDateTime, formatRupiah } from '@/lib/format'

export function OrdersPage() {
  const { data, isLoading } = useOrders()
  const orders = data?.orders ?? []

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Pesanan</h1>
        <p className="mt-1 text-muted">Riwayat pembayaran beserta cara dan waktunya.</p>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <Card>
          <EmptyState illustration="box"
            icon={<Receipt className="size-6" />}
            title="Belum ada pesanan"
            description="Pembelian kelas akan tercatat di sini beserta rincian pembayarannya."
            action={
              <Link to="/kelas">
                <Button>Jelajahi katalog</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <Card key={order.id}>
              <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <OrderStatusBadge state={order.state} />
                    <span className="text-xs text-subtle">{formatDateTime(order.created_at)}</span>
                  </div>
                  <p className="mt-2 font-bold text-ink">{order.course.title}</p>

                  <p className="mono-label mt-1.5 text-[11px] text-subtle">
                    {/* Saluran yang benar-benar dipakai baru diketahui setelah
                        pembeli membayar; sebelum itu belum ada yang dapat
                        disebut, dan menebaknya hanya akan keliru. */}
                    {namaSaluran(order.payment?.channel, order.method)}
                    {order.payment?.provider_ref ? ` · ${order.payment.provider_ref}` : ''}
                  </p>

                  {order.paid_at ? (
                    <p className="mt-1 text-xs text-subtle">
                      Dibayar {formatDateTime(order.paid_at)}
                    </p>
                  ) : null}
                </div>

                <div className="flex items-center gap-4">
                  <p className="text-lg font-extrabold tabular-nums text-ink">{formatRupiah(order.amount)}</p>
                  {order.state === 'pending' ? (
                    <Link to="/checkout/$orderId" params={{ orderId: order.id }}>
                      <Button size="sm">Bayar sekarang</Button>
                    </Link>
                  ) : order.state === 'paid' ? (
                    <Link to="/app/kelas-saya">
                      <Button size="sm" variant="outline">
                        Buka kelas
                      </Button>
                    </Link>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * namaSaluran menuliskan cara pembayaran yang benar-benar dipakai.
 *
 * Nama saluran penyedia — "bca_va", "gopay" — jauh lebih berarti bagi
 * pembeli daripada metode internal kita, karena itulah yang ia lihat dan
 * pilih sendiri di halaman pembayaran.
 */
function namaSaluran(saluran?: string, metode?: string): string {
  const daftar: Record<string, string> = {
    bca_va: 'BCA Virtual Account',
    bni_va: 'BNI Virtual Account',
    bri_va: 'BRI Virtual Account',
    permata_va: 'Permata Virtual Account',
    echannel: 'Mandiri Bill Payment',
    other_va: 'Virtual Account',
    bank_transfer: 'Transfer bank',
    qris: 'QRIS',
    gopay: 'GoPay',
    shopeepay: 'ShopeePay',
    credit_card: 'Kartu kredit',
  }

  if (saluran && daftar[saluran]) return daftar[saluran]
  if (saluran) return saluran.replace(/_/g, ' ')
  if (metode) return methodLabel[metode as keyof typeof methodLabel] ?? metode
  return 'Belum dipilih'
}
